import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { useTenant } from '@/contexts'
import type { OrgRole, LocationAssignment, LocationRole } from '@/types/team-members'
import {
  ASSIGNABLE_ROLES,
  ORG_ROLE_LABELS,
  ORG_ROLE_DESCRIPTIONS,
} from '@/types/team-members'
import { LOCATION_ROLES, LOCATION_ROLE_LABELS } from '@/types/location-users'

interface AddTeamMemberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: {
    email: string
    display_name?: string
    role: OrgRole
    location_assignments: LocationAssignment[]
  }) => Promise<void>
  isLoading?: boolean
}

interface LocationSelection {
  location_id: string
  selected: boolean
  location_role: LocationRole
}

export function AddTeamMemberDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: AddTeamMemberDialogProps) {
  const { locations } = useTenant()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [orgRole, setOrgRole] = useState<OrgRole>('member')
  const [locationSelections, setLocationSelections] = useState<LocationSelection[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize location selections when dialog opens or locations change
  const initializeLocations = () => {
    setLocationSelections(
      (locations || []).map((loc) => ({
        location_id: loc.id,
        selected: false,
        location_role: 'viewer' as LocationRole,
      }))
    )
  }

  // Re-initialize when locations load after dialog is already open
  useEffect(() => {
    if (open && locations && locations.length > 0 && locationSelections.length === 0) {
      initializeLocations()
    }
  }, [open, locations])

  // Reset form when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (open) {
      initializeLocations()
    } else {
      setEmail('')
      setDisplayName('')
      setOrgRole('member')
      setLocationSelections([])
    }
    onOpenChange(open)
  }

  const handleLocationToggle = (locationId: string, checked: boolean) => {
    setLocationSelections((prev) =>
      prev.map((sel) =>
        sel.location_id === locationId ? { ...sel, selected: checked } : sel
      )
    )
  }

  const handleLocationRoleChange = (locationId: string, role: LocationRole) => {
    setLocationSelections((prev) =>
      prev.map((sel) =>
        sel.location_id === locationId ? { ...sel, location_role: role } : sel
      )
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !orgRole) return

    const selectedLocations = locationSelections
      .filter((sel) => sel.selected)
      .map((sel) => ({
        location_id: sel.location_id,
        location_role: sel.location_role,
      }))

    setIsSubmitting(true)
    try {
      await onSubmit({
        email,
        display_name: displayName || undefined,
        role: orgRole,
        location_assignments: selectedLocations,
      })
      handleOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getLocationName = (locationId: string) => {
    const loc = locations?.find((l) => l.id === locationId)
    return loc ? `${loc.name}${loc.location_code ? ` (${loc.location_code})` : ''}` : locationId
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogDescription>
            Create a new user account and optionally assign them to specific locations
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting || isLoading}
              required
            />
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="display_name">Display Name</Label>
            <Input
              id="display_name"
              type="text"
              placeholder="John Doe"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={isSubmitting || isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Optional. If not provided, will use email prefix.
            </p>
          </div>

          {/* Organization Role */}
          <div className="space-y-2">
            <Label htmlFor="org_role">Organization Role *</Label>
            <Select
              value={orgRole}
              onValueChange={(value) => setOrgRole(value as OrgRole)}
              disabled={isSubmitting || isLoading}
            >
              <SelectTrigger id="org_role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASSIGNABLE_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    <span>{ORG_ROLE_LABELS[r]}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {ORG_ROLE_DESCRIPTIONS[orgRole]}
            </p>
          </div>

          {/* Location Access */}
          {locations && locations.length > 0 && (
            <div className="space-y-3">
              <Label>Location Access (optional)</Label>
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {locationSelections.map((sel) => (
                  <div
                    key={sel.location_id}
                    className="flex items-center justify-between p-3 hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`loc-${sel.location_id}`}
                        checked={sel.selected}
                        onCheckedChange={(checked) =>
                          handleLocationToggle(sel.location_id, checked as boolean)
                        }
                        disabled={isSubmitting || isLoading}
                      />
                      <label
                        htmlFor={`loc-${sel.location_id}`}
                        className="text-sm cursor-pointer"
                      >
                        {getLocationName(sel.location_id)}
                      </label>
                    </div>
                    {sel.selected && (
                      <Select
                        value={sel.location_role}
                        onValueChange={(value) =>
                          handleLocationRoleChange(sel.location_id, value as LocationRole)
                        }
                        disabled={isSubmitting || isLoading}
                      >
                        <SelectTrigger className="w-28 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LOCATION_ROLES.map((r) => (
                            <SelectItem key={r} value={r}>
                              {LOCATION_ROLE_LABELS[r]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Select locations this user should have access to. You can assign different roles per location.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting || isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!email.trim() || isSubmitting || isLoading || !orgRole}
            >
              {isSubmitting ? 'Creating...' : 'Add Team Member'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
